import * as amplitude from '@amplitude/analytics-browser';

import axios from '../axios';
import { oauthService } from '../services/OAuthService';
import { getTSPByKey } from '../tsps';
import { LoginFormData } from '../types/LoginFormData';

interface AuthRouterProps {
  TSP: string;
  serviceName: string;
  token: string;
  fleetCode: string;
  updatingTSP: boolean;
  serviceID: string;
  magicLink: boolean;
  redirectURISuccess?: string;
  clientIDs: {
    motiveClientID?: string;
    samsaraClientID?: string;
    zubieClientID?: string;
    bouncieClientID?: string;
  };
  onSuccess: () => void;
  onError: (error: any) => void;
  setRedirectURI: (uri: string) => void;
  setTspCount: (count: number) => void;
  currentTspCount: number; // Add current count so we can increment it
  analyticsContext?: any; // Add analytics context for webhook tracking
  linkState: string;
}

export class AuthRouter {
  private readonly isOAuthTSP: boolean;

  constructor(private props: AuthRouterProps) {
    const { TSP } = props;
    this.isOAuthTSP = ['motive', 'samsara', 'zubie', 'bouncie'].includes(TSP);
  }

  async handleFormSubmit(data: LoginFormData) {
    amplitude.track('Onboarding Form Submit', {
      tsp: this.props.TSP,
      serviceName: this.props.serviceName,
      hasDOTNumber: !!data.dotnumber,
      hasCarrierName: !!data.carriername,
      isUpdatingTSP: this.props.updatingTSP,
      skipCredentials: !!data.skipCredentials,
    });

    try {
      if (this.isOAuthTSP) {
        return await this.handleOAuthSubmit(data);
      }
      return await this.handleStandardSubmit(data);
    } catch (err) {
      console.log('Error in handleFormSubmit:', err);
      this.props.onError(err);
      throw err;
    }
  }

  private async handleOAuthSubmit(data: LoginFormData) {
    if (data.skipCredentials) {
      return this.redirectToOAuth();
    }

    try {
      if (data.username) {
        sessionStorage.setItem(
          `${this.props.TSP.toLowerCase()}_username`,
          data.username
        );
      }
      if (data.password) {
        sessionStorage.setItem(
          `${this.props.TSP.toLowerCase()}_password`,
          data.password
        );
      }
    } catch (error) {
      // Continue with OAuth flow even if sessionStorage fails
    }

    return this.redirectToOAuth();
  }

  private async handleStandardSubmit(data: LoginFormData) {
    // Send TSP_ONBOARDING_STARTED webhook when credentials are submitted
    if (this.props.analyticsContext) {
      const { Analytics } = await import('../utils/analytics');
      await Analytics.credentialsEntered(
        this.props.analyticsContext,
        this.props.TSP,
        'standard_credentials',
        this.props.updatingTSP
      );
    }

    const response = await this.saveCredentials(data);

    if (response.status === 201) {
      // Since API doesn't return tsp_count, we need to increment manually
      // For new connections (not updates), increment the current count by 1
      let finalTspCount = this.props.currentTspCount;
      if (!this.props.updatingTSP) {
        finalTspCount = this.props.currentTspCount + 1;
        console.log(
          '[AuthRouter] Success response - incrementing tspCount for new TSP connection from',
          this.props.currentTspCount,
          'to',
          finalTspCount,
          'API response:',
          response.data
        );
        this.props.setTspCount(finalTspCount);
      } else {
        console.log(
          '[AuthRouter] TSP update - not incrementing tspCount, API response:',
          response.data
        );
      }

      amplitude.track('Onboarding Success', {
        tsp: this.props.TSP,
        serviceName: this.props.serviceName,
        isUpdatingTSP: this.props.updatingTSP,
        tspCount: finalTspCount,
      });

      // Send TSP_ONBOARDING_COMPLETED webhook when data is successfully retrieved
      if (this.props.analyticsContext) {
        const { Analytics } = await import('../utils/analytics');
        await Analytics.connectionSuccessful(
          this.props.analyticsContext,
          this.props.TSP
        );
      }

      if (this.props.redirectURISuccess) {
        this.props.setRedirectURI(this.props.redirectURISuccess);
      } else if (this.props.magicLink) {
        this.props.setRedirectURI(process.env.REACT_APP_DEV_DASH || '');
      } else {
        this.props.setRedirectURI(response.data['RedirectURI']);
      }

      this.props.onSuccess();
      return response.data.RedirectURI;
    } else {
      throw new Error(`Unexpected response: ${response.status}`);
    }
  }

  private async saveCredentials(data: LoginFormData) {
    const { fleetCode, TSP, updatingTSP } = this.props;

    const response = await axios.post('/service/onboard', {
      Name: fleetCode,
      CarrierName: data.carriername,
      DOTNumber: data.dotnumber ? parseInt(data.dotnumber, 10) || 0 : 0,
      Username: data.username,
      Password: data.password,
      Database: data.database,
      Apikey: data.apikey,
      AuthorizationCode: data.authorizationcode,
      TSP: TSP,
      FTPUsername: data.FTPUsername,
      FTPPassword: data.FTPPassword,
      UpdateTSPConfirmed: updatingTSP ? true : false,
    });

    return response;
  }

  private redirectToOAuth() {
    const { TSP, clientIDs, serviceName } = this.props;
    const clientIDKey = `${TSP}ClientID` as keyof typeof clientIDs;
    const clientID = clientIDs[clientIDKey];

    if (!clientID) {
      const tspConfig = getTSPByKey(TSP);
      throw new Error(
        tspConfig
          ? `No ${tspConfig.name} app is associated with this account - please contact Axle support`
          : `Provider configuration not found for ${TSP}`
      );
    }

    // Use centralized OAuth service
    oauthService.initiateOAuth({
      providerKey: TSP,
      clientID,
      serviceName,
      linkState: this.props.linkState,
      onError: this.props.onError,
    });
  }
}
